import { Component, computed, inject, OnDestroy, signal } from "@angular/core";
import { CreditsService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./redemption-confirmation.component.scss";

type RedemptionOption = {
  id: string;
  label: string; // what user sees: "$1", "$5", "$10", "1month"
  amount: number; // currency amount for display (mocked)
  pointsRequired: number;
}; // points needed to redeem this option

type RedemptionMock = {
  title: string;
  userPoints: number;
  options: RedemptionOption[];
  terms: string[];
};

@Component({
  selector: "app-confirm-redemption",
  templateUrl: "./redemption-confirmation.component.html",
})
export class RedemptionConfirmationComponent implements OnDestroy {
  // UI constants used in the template
  readonly ITEM_MULTIPLIER = "x1";
  readonly POINTS_DISPLAY = "-1,000";
  creditsService = inject(CreditsService);
  i18n = inject(I18nService);
  // UI state for collapsible sections
  // Default closed as requested
  isWhatOpen = signal(false);
  isHowOpen = signal(false);

  private destroy$ = new Subject<void>();
  private customPointsChange$ = new Subject<number>();

  // Mock data (replace with real props/service later)
  redemption: RedemptionMock = {
    title: "Monthly Discount",
    userPoints: this.creditsService.totalGoldCredits(),
    options: [{ id: "opt-1m", label: "One Month", amount: 1000, pointsRequired: 1000 }],
    terms: [],
  };
  selectedOption = signal<RedemptionOption>(this.redemption.options[0]);

  customPoints = signal<number | null>(null);
  isCustomInputActive = signal(false);
  isRedeemAllActive = signal(true);

  remainingPoints = computed(() => {
    if (this.selectedOption()) {
      const required = this.selectedOption()?.pointsRequired ?? 0;
      return Math.max(0, this.redemption.userPoints - required);
    }

    if (this.isCustomInputActive()) {
      const custom = this.customPoints() ? this.customPoints() : 0;
      return Math.max(0, this.redemption.userPoints - custom);
    }

    if (this.isRedeemAllActive()) {
      return 0;
    }
  });

  selectedWorth = computed(() => {
    if (this.selectedOption()) {
      return this.selectedOption()?.amount / 1000;
    }

    if (this.isCustomInputActive()) {
      const custom = this.customPoints() ? this.customPoints() : 0;
      return custom / 1000;
    }

    if (this.isRedeemAllActive()) {
      return this.creditsService.totalGoldCredits() / 1000;
    }
  });

  selectedCrdts = computed(() => {
    return this.selectedWorth() * 1000;
  });

  // Lists used by the template to render repeated rows via *ngFor
  readonly whatYouGetItems: string[] = [
    "redemptionAutomaticRetrieval",
    "redemptionDirectConnectivity",
    "redemptionAdvancedCategorisation",
    "redemptionOneGBStorage",
  ];

  readonly howItWorksItems: { icon?: string; key: string }[] = [
    { icon: "lightning", key: "redemptionImmediateAccess" },
    { icon: "refresh", key: "redemptionSubscriptionExtended" },
    { icon: "info-white", key: "redemptionRefreshRequired" },
  ];

  // Provide computed getters for template bindings to avoid inline conditionals
  whatMaxHeight = computed(() => (this.isWhatOpen() ? "200px" : "0px"));
  howMaxHeight = computed(() => (this.isHowOpen() ? "200px" : "0px"));

  constructor() {
    // Set up debounced input handler (waits 500ms after user stops typing)
    this.customPointsChange$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.handleCustomPointsChange(value);
      });

    this.creditsService.creditAmountToRedeem.set(this.selectedOption().pointsRequired);
  }

  toggleWhat(): void {
    this.isWhatOpen.set(!this.isWhatOpen());
  }

  toggleHow(): void {
    this.isHowOpen.set(!this.isHowOpen());
  }

  onCustomPointsInput(value: number | null): void {
    // Deselect predefined options and redeem all when typing in custom input
    this.selectedOption.set(null);
    this.isRedeemAllActive.set(false);
    this.isCustomInputActive.set(true);

    if (value !== null) {
      this.customPointsChange$.next(value);
    }
  }

  onCustomAmountClick(): void {
    this.isCustomInputActive.set(true);
    this.isRedeemAllActive.set(false);
    this.selectedOption.set(null);
  }

  private handleCustomPointsChange(value: number): void {
    // Validate and process the custom points value
    if (value < 1) {
      this.customPoints.set(1);
    } else if (value > this.redemption.userPoints) {
      this.customPoints.set(this.redemption.userPoints);
    }

    // Your logic here - e.g., update the service or calculate something
    this.creditsService.creditAmountToRedeem.set(value);
  }

  redeemAllPoints(): void {
    // Deselect all other options and clear custom input
    this.selectedOption.set(null);
    this.customPoints.set(null);
    this.isCustomInputActive.set(false);
    this.isRedeemAllActive.set(true);

    this.creditsService.creditAmountToRedeem.set(this.creditsService.totalGoldCredits());
  }

  canAffordOption(opt: RedemptionOption): boolean {
    return this.redemption.userPoints >= opt.pointsRequired;
  }

  selectOption(opt: RedemptionOption): void {
    if (!this.canAffordOption(opt)) {
      return;
    }
    // Deselect custom input and redeem all when selecting a predefined option
    this.customPoints.set(null);
    this.isCustomInputActive.set(false);
    this.isRedeemAllActive.set(false);
    this.selectedOption.set(opt);
    this.creditsService.creditAmountToRedeem.set(opt.pointsRequired);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
